const FAVORITES_KEY = 'favorite_cocktails';

export class FavoritesService {
  /**
   * Get all favorite cocktail IDs from localStorage
   */
  static getFavorites(): string[] {
    try {
      const favorites = localStorage.getItem(FAVORITES_KEY);
      return favorites ? JSON.parse(favorites) : [];
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error getting favorites from localStorage:', error);
      }
      // Clear corrupted data
      try {
        localStorage.removeItem(FAVORITES_KEY);
      } catch {
        // Ignore errors during cleanup
      }
      return [];
    }
  }

  /**
   * Add a cocktail ID to favorites
   */
  static addFavorite(cocktailId: string): void {
    try {
      // Validate cocktailId
      if (!cocktailId || typeof cocktailId !== 'string' || cocktailId.trim().length === 0) {
        return;
      }
      const favorites = this.getFavorites();
      if (!favorites.includes(cocktailId)) {
        favorites.push(cocktailId);
        localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error adding favorite to localStorage:', error);
      }
      // Handle QuotaExceededError
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        if (process.env.NODE_ENV === 'development') {
          console.warn('LocalStorage quota exceeded. Cannot add more favorites.');
        }
      }
    }
  }

  /**
   * Remove a cocktail ID from favorites
   */
  static removeFavorite(cocktailId: string): void {
    try {
      const favorites = this.getFavorites();
      const updatedFavorites = favorites.filter(id => id !== cocktailId);
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(updatedFavorites));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error removing favorite from localStorage:', error);
      }
    }
  }

  /**
   * Check if a cocktail ID is in favorites
   */
  static isFavorite(cocktailId: string): boolean {
    const favorites = this.getFavorites();
    return favorites.includes(cocktailId);
  }

  /**
   * Toggle favorite status for a cocktail ID
   */
  static toggleFavorite(cocktailId: string): boolean {
    if (this.isFavorite(cocktailId)) {
      this.removeFavorite(cocktailId);
      return false; // Now not favorited
    } else {
      this.addFavorite(cocktailId);
      return true; // Now favorited
    }
  }

  /**
   * Clear all favorites
   */
  static clearFavorites(): void {
    try {
      localStorage.removeItem(FAVORITES_KEY);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error clearing favorites from localStorage:', error);
      }
    }
  }

  /**
   * Get the count of favorites
   */
  static getFavoritesCount(): number {
    return this.getFavorites().length;
  }
}



